import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  GridLegacy as Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Autocomplete,
  Switch,
  FormControlLabel,
  Rating,
  InputAdornment,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from '@mui/material';
import {
  DateTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Trade } from '../../contexts/TradingJournalContext';

interface TradeFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (trade: Omit<Trade, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  initialData?: Partial<Trade>;
  mode?: 'add' | 'edit';
}

const COMMON_SYMBOLS = {
  Weltrade: [
    // Major Forex pairs
    'EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD', 'USDCAD', 'NZDUSD',
    // Minor Forex pairs
    'EURJPY', 'GBPJPY', 'EURGBP', 'AUDCAD', 'AUDNZD', 'CADCHF', 'CADJPY',
    'CHFJPY', 'EURCHF', 'EURAUD', 'EURNZD', 'GBPAUD', 'GBPCAD', 'GBPCHF',
    'GBPNZD', 'NZDCAD', 'NZDCHF', 'NZDJPY',
    // Commodities
    'XAUUSD', 'XAGUSD', 'XTIUSD', 'XBRUSD',
    // Indices
    'US30', 'US500', 'NAS100', 'GER30', 'UK100', 'JPN225',
    // Crypto
    'BTCUSD', 'ETHUSD', 'LTCUSD', 'XRPUSD',
    // Pain and Gain Indices (SyntX)
    'PAINX400', 'PAINX600', 'PAINX800', 'PAINX999', 'PAINX1200',
    'GAINX400', 'GAINX600', 'GAINX800', 'GAINX999', 'GAINX1200'
  ],
  Deriv: [
    // Major Forex pairs
    'EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD', 'USDCAD', 'NZDUSD',
    // Volatility Indices
    'R_10', 'R_25', 'R_50', 'R_75', 'R_100',
    'V10_1S', 'V25_1S', 'V50_1S', 'V75_1S', 'V100_1S',
    // Boom and Crash Indices
    'BOOM300', 'BOOM500', 'BOOM1000',
    'CRASH300', 'CRASH500', 'CRASH1000',
    // Jump Indices
    'JMP10', 'JMP25', 'JMP50', 'JMP75', 'JMP100',
    // Step Indices
    'STEP', 'STEP200', 'STEP500',
    // Range Break Indices
    'RNGBRK100', 'RNGBRK200',
    // Daily Reset Indices
    'RDBULL', 'RDBEAR',
    // Basket Indices
    'WLDAUD', 'WLDEUR', 'WLDGBP', 'WLDUSD', 'WLDXAU',
    // Commodities
    'XAUUSD', 'XAGUSD', 'XTIUSD', 'XBRUSD',
    // Crypto
    'BTCUSD', 'ETHUSD', 'LTCUSD'
  ],
  XM: [
    // Major Forex pairs
    'EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD', 'USDCAD', 'NZDUSD',
    // Minor Forex pairs
    'EURGBP', 'EURJPY', 'EURCHF', 'EURAUD', 'GBPJPY', 'GBPCHF', 'AUDJPY',
    'AUDCAD', 'AUDCHF', 'AUDNZD', 'CADCHF', 'CADJPY', 'CHFJPY', 'EURAUD',
    'EURCAD', 'EURNOK', 'EURNZD', 'EURSEK', 'GBPAUD', 'GBPCAD', 'GBPNZD',
    'NZDCAD', 'NZDCHF', 'NZDJPY',
    // Exotic Forex pairs
    'EURTRY', 'GBPTRY', 'USDTRY', 'USDZAR', 'USDSEK', 'USDNOK', 'USDPLN',
    'USDHUF', 'USDCZK', 'USDDKK', 'USDSGD', 'USDHKD', 'USDMXN',
    // Commodities
    'XAUUSD', 'XAGUSD', 'XTIUSD', 'XBRUSD', 'XPTUSD', 'XPDUSD',
    'COCOA', 'COFFE', 'CORN', 'COTTO', 'SBEAN', 'SUGAR', 'WHEAT',
    // Indices
    'US30', 'US500', 'NAS100', 'UK100', 'GER30', 'FRA40', 'JPN225',
    'AUS200', 'ESP35', 'HK50', 'IT40', 'NETH25', 'SWI20', 'SING',
    // Energies
    'NGAS', 'GSOIL'
  ]
};

// Popular Forex/CFD and Crypto brokers
const POPULAR_BROKERS = [
  // Forex & Synthetics
  { name: 'Weltrade', category: 'Forex/Synthetics' },
  { name: 'Deriv', category: 'Synthetics/Forex' },
  { name: 'XM', category: 'Forex/CFDs' },
  { name: 'OANDA', category: 'Forex' },
  { name: 'FXCM', category: 'Forex/CFDs' },
  { name: 'Pepperstone', category: 'Forex/CFDs' },
  { name: 'IC Markets', category: 'Forex/CFDs' },
  { name: 'Admiral Markets', category: 'Forex/CFDs' },
  { name: 'FP Markets', category: 'Forex/CFDs' },
  { name: 'AvaTrade', category: 'Forex/CFDs' },
  { name: 'ActivTrades', category: 'Forex/CFDs' },
  { name: 'Vantage', category: 'Forex/CFDs' },
  { name: 'FxPro', category: 'Forex/CFDs' },
  { name: 'ThinkMarkets', category: 'Forex/CFDs' },
  { name: 'Tickmill', category: 'Forex/CFDs' },
  { name: 'Exness', category: 'Forex/CFDs' },
  { name: 'HotForex', category: 'Forex/CFDs' },
  { name: 'FBS', category: 'Forex/CFDs' },
  { name: 'InstaForex', category: 'Forex' },
  { name: 'Alpari', category: 'Forex/CFDs' },
  { name: 'LiteForex', category: 'Forex/CFDs' },
  { name: 'RoboForex', category: 'Forex/CFDs' },
  
  // CFDs & Spread Betting
  { name: 'IG Markets', category: 'CFDs/Spread Betting' },
  { name: 'Plus500', category: 'CFDs' },
  { name: 'eToro', category: 'Social Trading/CFDs' },
  { name: 'CMC Markets', category: 'CFDs/Spread Betting' },
  { name: 'City Index', category: 'CFDs/Spread Betting' },
  
  // Cryptocurrency
  { name: 'Binance', category: 'Cryptocurrency' },
  { name: 'Coinbase', category: 'Cryptocurrency' },
  { name: 'Kraken', category: 'Cryptocurrency' },
  { name: 'Bybit', category: 'Cryptocurrency' },
  { name: 'OKX', category: 'Cryptocurrency' },
  { name: 'Bitfinex', category: 'Cryptocurrency' },
  { name: 'KuCoin', category: 'Cryptocurrency' },
  { name: 'Huobi', category: 'Cryptocurrency' },
  { name: 'Gate.io', category: 'Cryptocurrency' },
  { name: 'Crypto.com', category: 'Cryptocurrency' },
  { name: 'Phemex', category: 'Cryptocurrency' },
  { name: 'BitMEX', category: 'Cryptocurrency' },
  { name: 'Deribit', category: 'Cryptocurrency' }
];

const STRATEGIES = [
  'Scalping', 'Day Trading', 'Swing Trading', 'Position Trading',
  'Trend Following', 'Mean Reversion', 'Breakout', 'Support/Resistance',
  'Moving Average Crossover', 'RSI Divergence', 'MACD Strategy',
  'Fibonacci Retracement', 'Price Action', 'News Trading'
];

const TIMEFRAMES = [
  'M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1', 'MN1'
];

const SETUPS = [
  'Bull Flag', 'Bear Flag', 'Triangle', 'Double Top', 'Double Bottom',
  'Head and Shoulders', 'Wedge', 'Channel', 'Hammer', 'Doji',
  'Engulfing', 'Pin Bar', 'Inside Bar', 'Cup and Handle'
];

const steps = ['Trade Details', 'Risk Management', 'Psychology & Notes'];

// Function to get symbol description for better UX
const getSymbolDescription = (symbol: string, broker?: string) => {
  const descriptions: { [key: string]: string } = {
    // Major Forex
    'EURUSD': 'Euro / US Dollar',
    'GBPUSD': 'British Pound / US Dollar',
    'USDJPY': 'US Dollar / Japanese Yen',
    'USDCHF': 'US Dollar / Swiss Franc',
    'AUDUSD': 'Australian Dollar / US Dollar',
    'USDCAD': 'US Dollar / Canadian Dollar',
    'NZDUSD': 'New Zealand Dollar / US Dollar',
    
    // Commodities
    'XAUUSD': 'Gold Spot',
    'XAGUSD': 'Silver Spot',
    'XTIUSD': 'Crude Oil (WTI)',
    'XBRUSD': 'Brent Crude Oil',
    
    // Crypto
    'BTCUSD': 'Bitcoin',
    'ETHUSD': 'Ethereum',
    
    // Deriv Synthetics
    'BOOM300': 'Boom 300 Index',
    'BOOM500': 'Boom 500 Index',
    'BOOM1000': 'Boom 1000 Index',
    'CRASH300': 'Crash 300 Index',
    'CRASH500': 'Crash 500 Index',
    'CRASH1000': 'Crash 1000 Index',
    'R_10': 'Volatility 10 Index',
    'R_25': 'Volatility 25 Index',
    'R_50': 'Volatility 50 Index',
    'R_75': 'Volatility 75 Index',
    'R_100': 'Volatility 100 Index',
    
    // Weltrade Pain/Gain
    'PAINX400': 'Pain X 400 (400% leverage)',
    'PAINX600': 'Pain X 600 (600% leverage)',
    'PAINX800': 'Pain X 800 (800% leverage)',
    'PAINX999': 'Pain X 999 (999% leverage)',
    'PAINX1200': 'Pain X 1200 (1200% leverage)',
    'GAINX400': 'Gain X 400 (400% leverage)',
    'GAINX600': 'Gain X 600 (600% leverage)',
    'GAINX800': 'Gain X 800 (800% leverage)',
    'GAINX999': 'Gain X 999 (999% leverage)',
    'GAINX1200': 'Gain X 1200 (1200% leverage)',
    
    // Indices
    'US30': 'Dow Jones Industrial Average',
    'US500': 'S&P 500 Index',
    'NAS100': 'NASDAQ 100 Index',
    'GER30': 'DAX 30 Index',
    'UK100': 'FTSE 100 Index',
    'JPN225': 'Nikkei 225 Index',
  };
  
  return descriptions[symbol] || 'Manual entry';
};

const TradeForm = ({
  open,
  onClose,
  onSubmit,
  initialData,
  mode = 'add'
}: TradeFormProps): React.ReactElement => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Map each field to the wizard step in which it is displayed
  const fieldStepMap: Record<string, number> = {
    broker: 0,
    symbol: 0,
    type: 0,
    entryPrice: 0,
    quantity: 0,
    status: 0,
    entryTime: 0,
    exitPrice: 0,
    exitTime: 0,
    stopLoss: 1,
    takeProfit: 1,
    commission: 1,
    swap: 1,
    pnl: 1,
    strategy: 1,
    timeframe: 1,
    setup: 1,
    confidence: 2,
    emotion: 2,
    marketCondition: 2,
    tags: 2,
    notes: 2,
  };

  const getStepForField = (field: string): number => fieldStepMap[field] ?? 0;

  const [formData, setFormData] = useState<Partial<Trade>>({
    broker: '',
    symbol: '',
    type: 'buy',
    entryPrice: 0,
    exitPrice: undefined,
    quantity: 0,
    entryTime: new Date(),
    exitTime: undefined,
    stopLoss: undefined,
    takeProfit: undefined,
    commission: 0,
    swap: 0,
    status: 'open',
    strategy: '',
    notes: '',
    tags: [],
    confidence: 3,
    emotion: 'neutral',
    marketCondition: 'calm',
    timeframe: 'H1',
    setup: '',
    screenshots: [],
    pnl: undefined,
    ...initialData
  });

  // Custom sorted broker list: show XM, Weltrade, Deriv first (Featured Brokers), then others alphabetically within categories.
  const FEATURED = ['XM', 'Weltrade', 'Deriv'];

  const brokerOptions = React.useMemo(() => {
    const withCustomCategory = POPULAR_BROKERS.map((b) => ({
      ...b,
      customCategory: FEATURED.includes(b.name) ? 'Featured Brokers' : b.category,
    }));

    const priority = (name: string) => {
      const idx = FEATURED.indexOf(name);
      return idx === -1 ? Infinity : idx; // XM=0, Weltrade=1, Deriv=2, others Infinity
    };

    const sorted = withCustomCategory.sort((a, b) => {
      // Featured category first
      if (a.customCategory !== b.customCategory) {
        if (a.customCategory === 'Featured Brokers') return -1;
        if (b.customCategory === 'Featured Brokers') return 1;
        return a.customCategory.localeCompare(b.customCategory);
      }

      // Inside Featured category use explicit priority
      if (a.customCategory === 'Featured Brokers') {
        return priority(a.name) - priority(b.name);
      }

      // For non-featured brokers alphabetical by name
      return a.name.localeCompare(b.name);
    });

    return sorted.map((b) => b.name);
  }, []);

  // Unique symbol options for selected broker
  const symbolOptions = React.useMemo(() => {
    const raw = COMMON_SYMBOLS[formData.broker as keyof typeof COMMON_SYMBOLS] || [];
    return Array.from(new Set(raw));
  }, [formData.broker]);

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const handleInputChange = (field: keyof Trade, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleTagsChange = (event: any, newValue: string[]) => {
    handleInputChange('tags', newValue);
  };

  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.symbol) newErrors.symbol = 'Symbol is required';
    if (!formData.entryPrice || formData.entryPrice <= 0) newErrors.entryPrice = 'Entry price must be greater than 0';
    if (!formData.quantity || formData.quantity <= 0) newErrors.quantity = 'Quantity must be greater than 0';
    if (!formData.entryTime) newErrors.entryTime = 'Entry time is required';
    if (formData.commission !== undefined && formData.commission < 0) newErrors.commission = 'Commission cannot be negative';
    
    if (formData.status === 'closed' && (!formData.exitPrice || formData.exitPrice <= 0)) {
      newErrors.exitPrice = 'Exit price is required for closed trades';
    }
    
    if (formData.status === 'closed' && (typeof formData.pnl !== 'number' || isNaN(formData.pnl))) {
      newErrors.pnl = 'Profit / Loss is required for closed trades';
    }
    
    if (formData.stopLoss && formData.entryPrice && formData.type === 'buy' && formData.stopLoss >= formData.entryPrice) {
      newErrors.stopLoss = 'Stop loss must be below entry price for buy trades';
    }
    
    if (formData.stopLoss && formData.entryPrice && formData.type === 'sell' && formData.stopLoss <= formData.entryPrice) {
      newErrors.stopLoss = 'Stop loss must be above entry price for sell trades';
    }
    
    if (formData.takeProfit && formData.entryPrice && formData.type === 'buy' && formData.takeProfit <= formData.entryPrice) {
      newErrors.takeProfit = 'Take profit must be above entry price for buy trades';
    }
    
    if (formData.takeProfit && formData.entryPrice && formData.type === 'sell' && formData.takeProfit >= formData.entryPrice) {
      newErrors.takeProfit = 'Take profit must be below entry price for sell trades';
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Navigate to the first step containing an error so the user can see it
      const firstErrorField = Object.keys(validationErrors)[0];
      setActiveStep(getStepForField(firstErrorField));
      return;
    }
    
    try {
      setLoading(true);
      await onSubmit(formData as Omit<Trade, 'id' | 'createdAt' | 'updatedAt'>);
      onClose();
      
      // Reset form
      setFormData({
        broker: '',
        symbol: '',
        type: 'buy',
        entryPrice: 0,
        exitPrice: undefined,
        quantity: 0,
        entryTime: new Date(),
        exitTime: undefined,
        stopLoss: undefined,
        takeProfit: undefined,
        commission: 0,
        swap: 0,
        status: 'open',
        strategy: '',
        notes: '',
        tags: [],
        confidence: 3,
        emotion: 'neutral',
        marketCondition: 'calm',
        timeframe: 'H1',
        setup: '',
        screenshots: [],
        pnl: undefined,
      });
      setActiveStep(0);
      setErrors({});
    } catch (error) {
      console.error('Error submitting trade:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        // Stay on or navigate to step with error
        const firstErrorField = Object.keys(validationErrors)[0];
        setActiveStep(getStepForField(firstErrorField));
        return;
      }

      // Validate basic trade details before proceeding
      const basicFields = ['symbol', 'entryPrice', 'quantity', 'entryTime'];
      const hasErrors = basicFields.some(field => {
        if (field === 'symbol' && !formData.symbol) return true;
        if (field === 'entryPrice' && (!formData.entryPrice || formData.entryPrice <= 0)) return true;
        if (field === 'quantity' && (!formData.quantity || formData.quantity <= 0)) return true;
        if (field === 'entryTime' && !formData.entryTime) return true;
        return false;
      });
      
      if (hasErrors) {
        validateForm();
        return;
      }
    }
    
    if (activeStep === 1) {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        const firstErrorField = Object.keys(validationErrors)[0];
        setActiveStep(getStepForField(firstErrorField));
        return;
      }
    }
    
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={brokerOptions}
                value={formData.broker || ''}
                onChange={(event, newValue) => handleInputChange('broker', newValue || '')}
                onInputChange={(event, newInputValue) => handleInputChange('broker', newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Broker"
                    error={!!errors.broker}
                    helperText={errors.broker || "Search for broker or type custom name"}
                    required
                    placeholder="Search brokers..."
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  const broker = POPULAR_BROKERS.find(b => b.name === option);
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {option}
                        </Typography>
                        {broker && (
                          <Typography variant="caption" color="text.secondary">
                            {broker.category}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }}
                groupBy={(option) => {
                  if (FEATURED.includes(option)) return 'Featured Brokers';
                  const broker = POPULAR_BROKERS.find(b => b.name === option);
                  return broker ? broker.category : 'Other';
                }}
                filterOptions={(options, { inputValue }) => {
                  const filtered = options.filter(option =>
                    option.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  
                  // If input doesn't match any existing broker, show it as a custom option
                  if (inputValue !== '' && !filtered.includes(inputValue)) {
                    filtered.push(inputValue);
                  }
                  
                  return filtered;
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={symbolOptions}
                value={formData.symbol || ''}
                onChange={(event, newValue) => handleInputChange('symbol', newValue || '')}
                onInputChange={(event, newInputValue) => handleInputChange('symbol', newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Symbol"
                    error={!!errors.symbol}
                    helperText={errors.symbol || 
                      (COMMON_SYMBOLS[formData.broker as keyof typeof COMMON_SYMBOLS] 
                        ? "Start typing or select from broker-specific suggestions" 
                        : "Enter the trading symbol (e.g., EURUSD, XAUUSD, BTCUSD)")
                    }
                    required
                    placeholder="e.g., EURUSD, XAUUSD, BOOM1000"
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {option}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getSymbolDescription(option, formData.broker)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Trade Type</InputLabel>
                <Select
                  value={formData.type || 'buy'}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  label="Trade Type"
                  startAdornment={
                    <InputAdornment position="start">
                      {formData.type === 'buy' ? (
                        <TrendingUpIcon color="success" />
                      ) : (
                        <TrendingDownIcon color="error" />
                      )}
                    </InputAdornment>
                  }
                >
                  <MenuItem value="buy">Buy (Long)</MenuItem>
                  <MenuItem value="sell">Sell (Short)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Entry Price"
                type="number"
                value={formData.entryPrice || ''}
                onChange={(e) => handleInputChange('entryPrice', parseFloat(e.target.value) || 0)}
                error={!!errors.entryPrice}
                helperText={errors.entryPrice}
                required
                inputProps={{ step: 0.00001, min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity/Lot Size"
                type="number"
                value={formData.quantity ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    handleInputChange('quantity', undefined);
                  } else {
                    const parsedValue = parseFloat(value);
                    if (!isNaN(parsedValue)) {
                      handleInputChange('quantity', parsedValue);
                    }
                  }
                }}
                error={!!errors.quantity}
                helperText={errors.quantity}
                required
                inputProps={{ step: 0.001, min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || 'open'}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Entry Time"
                  value={formData.entryTime}
                  onChange={(date: Date | null) => handleInputChange('entryTime', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.entryTime,
                      helperText: errors.entryTime,
                      required: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {formData.status === 'closed' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Exit Price"
                    type="number"
                    value={formData.exitPrice || ''}
                    onChange={(e) => handleInputChange('exitPrice', parseFloat(e.target.value) || undefined)}
                    error={!!errors.exitPrice}
                    helperText={errors.exitPrice}
                    required={formData.status === 'closed'}
                    inputProps={{ step: 0.00001, min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label="Exit Time"
                      value={formData.exitTime}
                      onChange={(date: Date | null) => handleInputChange('exitTime', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stop Loss"
                type="number"
                value={formData.stopLoss || ''}
                onChange={(e) => handleInputChange('stopLoss', parseFloat(e.target.value) || undefined)}
                error={!!errors.stopLoss}
                helperText={errors.stopLoss}
                inputProps={{ step: 0.00001 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Take Profit"
                type="number"
                value={formData.takeProfit || ''}
                onChange={(e) => handleInputChange('takeProfit', parseFloat(e.target.value) || undefined)}
                error={!!errors.takeProfit}
                helperText={errors.takeProfit}
                inputProps={{ step: 0.00001 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commission"
                type="number"
                value={formData.commission || 0}
                onChange={(e) => handleInputChange('commission', parseFloat(e.target.value) || 0)}
                error={!!errors.commission}
                helperText={errors.commission}
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Swap"
                type="number"
                value={formData.swap || 0}
                onChange={(e) => handleInputChange('swap', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.01 }}
              />
            </Grid>

            {formData.status === 'closed' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Profit / Loss"
                  type="number"
                  value={formData.pnl ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      handleInputChange('pnl', undefined);
                    } else if (value === '-') {
                      // Allow typing the minus sign
                      return;
                    } else {
                      const parsedValue = parseFloat(value);
                      if (!isNaN(parsedValue)) {
                        handleInputChange('pnl', parsedValue);
                      }
                    }
                  }}
                  error={!!errors.pnl}
                  helperText={errors.pnl}
                  inputProps={{ step: 0.01 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={STRATEGIES}
                value={formData.strategy || ''}
                onChange={(event, newValue) => handleInputChange('strategy', newValue || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Strategy" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={formData.timeframe || 'H1'}
                  onChange={(e) => handleInputChange('timeframe', e.target.value)}
                  label="Timeframe"
                >
                  {TIMEFRAMES.map(tf => (
                    <MenuItem key={tf} value={tf}>{tf}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={SETUPS}
                value={formData.setup || ''}
                onChange={(event, newValue) => handleInputChange('setup', newValue || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Setup/Pattern" />
                )}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography component="legend" gutterBottom>
                  Confidence Level
                </Typography>
                <Rating
                  value={formData.confidence || 3}
                  onChange={(event, newValue) => handleInputChange('confidence', newValue)}
                  max={5}
                  size="large"
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Emotion</InputLabel>
                <Select
                  value={formData.emotion || 'neutral'}
                  onChange={(e) => handleInputChange('emotion', e.target.value)}
                  label="Emotion"
                >
                  <MenuItem value="confident">Confident</MenuItem>
                  <MenuItem value="fearful">Fearful</MenuItem>
                  <MenuItem value="greedy">Greedy</MenuItem>
                  <MenuItem value="neutral">Neutral</MenuItem>
                  <MenuItem value="disciplined">Disciplined</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Market Condition</InputLabel>
                <Select
                  value={formData.marketCondition || 'calm'}
                  onChange={(e) => handleInputChange('marketCondition', e.target.value)}
                  label="Market Condition"
                >
                  <MenuItem value="trending">Trending</MenuItem>
                  <MenuItem value="ranging">Ranging</MenuItem>
                  <MenuItem value="volatile">Volatile</MenuItem>
                  <MenuItem value="calm">Calm</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.tags || []}
                onChange={handleTagsChange}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        variant="outlined"
                        label={option}
                        key={key}
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Add tags" />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Trade analysis, lessons learned, market observations..."
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          bgcolor: 'background.paper',
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {mode === 'edit' ? 'Edit Trade' : 'Add New Trade'}
          </Typography>
          <Button
            onClick={onClose}
            color="inherit"
            startIcon={<CloseIcon />}
            sx={{ 
              minWidth: 'auto',
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover'
              }
            }}
          >
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: 'background.paper' }}>
        <Box sx={{ width: '100%', mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Please correct the errors before proceeding.
          </Alert>
        )}

        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          {renderStepContent(activeStep)}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        borderTop: '1px solid', 
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>
        
        <Box sx={{ flex: '1 1 auto' }} />
        
        {activeStep === steps.length - 1 ? (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            size="large"
          >
            {loading ? 'Saving...' : mode === 'edit' ? 'Update Trade' : 'Add Trade'}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            variant="contained"
            size="large"
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TradeForm; 